import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "../use-access-token";
import { callLcosService } from "../../data/lcosServices";
import type { StaffMember } from "../use-user";
import { centerStaffQueryKey } from "../use-center";

interface UseStaffPhotoUploadProps {
  staffId?: string;
  centerId: string;
  onSuccess?: (url: string) => void;
  onSettled?: () => void;
}

interface PhotoUploadResponse {
  photoUrl: string;
}

const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png"];
const REQUIRED_DIMENSIONS = { width: 250, height: 250 };

const validateImage = (file: File): Promise<boolean> => {
  // Validate file type
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    throw new Error("Please upload a valid JPEG or PNG file");
  }

  // Validate dimensions
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      if (img.width !== REQUIRED_DIMENSIONS.width || img.height !== REQUIRED_DIMENSIONS.height) {
        reject(new Error(`Image must be exactly ${REQUIRED_DIMENSIONS.width}x${REQUIRED_DIMENSIONS.height} pixels`));
      } else {
        resolve(true);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to load image"));
    };

    img.src = objectUrl;
  });
};

export const useStaffPhotoUpload = ({ staffId, centerId, onSuccess, onSettled }: UseStaffPhotoUploadProps) => {
  const { getToken } = useAccessToken();
  const queryClient = useQueryClient();

  const uploadPhotoMutation = useMutation<PhotoUploadResponse, Error, File>({
    mutationFn: async (file: File) => {
      if (!staffId) {
        throw new Error("Staff ID is required");
      }
      // Validate image before upload
      await validateImage(file);

      const accessToken = await getToken();
      const formData = new FormData();
      formData.append("photo", file);

      return callLcosService<PhotoUploadResponse>(
        accessToken,
        `/api/staff/${centerId}/${staffId}/photo`,
        "POST",
        formData,
        true
      );
    },
    onSuccess: (data: PhotoUploadResponse) => {
      // Update staff data with new photo URL
      if (staffId) {
        queryClient.invalidateQueries<StaffMember>(centerStaffQueryKey(centerId));
      }
      window.toasts.add({
        content: "Photo uploaded successfully",
        type: "success"
      });
      onSuccess?.(data.photoUrl);
    },
    onSettled: onSettled
  });

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    await uploadPhotoMutation.mutateAsync(file);
  };

  return {
    handleUpload,
    isUploading: uploadPhotoMutation.isLoading
  };
};
